import { useState } from "react";
import { supabase } from "../lib/supabaseClient";

type RiskLevel = "low" | "medium" | "high" | "critical";

type AdvisorResult = {
  risk_level: RiskLevel;
  confidence: number;
  assessment: string;
  evidence: string[];
  likely_failure_modes: string[];
  recommended_actions: string[];
  urgency: string;
  recommended_status?: "Operational" | "Warning" | "Critical";
};

type MaintenanceAdvisorResponse = {
  advisor: AdvisorResult;
  context?: unknown[];
};

type MaintenanceAdvisorProps = {
  asset_id: string;
  userQuery?: string;
  className?: string;
};

const riskStyles: Record<RiskLevel, string> = {
  low: "border-emerald-200 bg-emerald-50 text-emerald-800",
  medium: "border-yellow-200 bg-yellow-50 text-yellow-800",
  high: "border-red-200 bg-red-50 text-red-800",
  critical: "border-red-300 bg-red-100 text-red-900",
};

const riskBadgeStyles: Record<RiskLevel, string> = {
  low: "bg-emerald-600 text-white",
  medium: "bg-yellow-500 text-slate-950",
  high: "bg-red-600 text-white",
  critical: "bg-red-800 text-white",
};

function formatConfidence(value: number) {
  if (!Number.isFinite(value)) return "N/A";
  return `${Math.round(value * 100)}%`;
}

export default function MaintenanceAdvisor({
  asset_id,
  userQuery = "Assess current preventive maintenance risk using recent logs and technical maintenance guidance.",
  className = "",
}: MaintenanceAdvisorProps) {
  const [analysisResult, setAnalysisResult] = useState<AdvisorResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  async function runRiskAssessment() {
    if (!asset_id) {
      setErrorMessage("Asset ID is required to run an AI risk assessment.");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const { data, error } = await supabase.functions.invoke<MaintenanceAdvisorResponse>("maintenance-advisor", {
        body: {
          asset_id,
          user_query: userQuery,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.advisor) {
        throw new Error("No advisor result returned.");
      }

      setAnalysisResult(data.advisor);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "AI risk assessment failed.");
    } finally {
      setLoading(false);
    }
  }

  const riskLevel = analysisResult?.risk_level ?? "low";

  return (
    <section className={`rounded-lg border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Predictive Maintenance Advisor</h3>
          <p className="mt-1 text-sm text-slate-500">Analyze failure risk using maintenance history and guidelines.</p>
        </div>

        <button
          type="button"
          onClick={runRiskAssessment}
          disabled={loading || !asset_id}
          className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading && (
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          )}
          {loading ? "Analyzing..." : "Run AI Risk Assessment"}
        </button>
      </div>

      {errorMessage && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {analysisResult && (
        <div className={`mt-5 rounded-lg border p-4 ${riskStyles[riskLevel]}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-8">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide opacity-80">Risk Level</p>
                <h4 className="mt-1 text-xl font-bold capitalize">{analysisResult.risk_level}</h4>
              </div>
              {analysisResult.recommended_status && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-80">Recommended Status</p>
                  <h4 className="mt-1 text-xl font-bold capitalize">{analysisResult.recommended_status}</h4>
                </div>
              )}
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${riskBadgeStyles[riskLevel]}`}>
              Confidence {formatConfidence(analysisResult.confidence)}
            </span>
          </div>

          <p className="mt-4 text-sm leading-6">{analysisResult.assessment}</p>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase opacity-80">Evidence</p>
              <ul className="mt-2 space-y-1 text-sm">
                {analysisResult.evidence.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase opacity-80">Failure Modes</p>
              <ul className="mt-2 space-y-1 text-sm">
                {analysisResult.likely_failure_modes.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase opacity-80">Recommended Actions</p>
              <ul className="mt-2 space-y-1 text-sm">
                {analysisResult.recommended_actions.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-4 rounded-md bg-white/60 px-3 py-2 text-sm font-medium">
            Urgency: {analysisResult.urgency}
          </div>
        </div>
      )}
    </section>
  );
}
